<template>
  <div v-if="documentType" class="flex flex-col h-full overflow-hidden">
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="modifiedDocumentType?.name ? modifiedDocumentType.name : '-'"
      link-name="document-types"
      :mode="mode"
      :is-modified="isModified"
      :is-edit-active="documentType.isFromManager || false"
      @edit-mode="mode = 'edit'"
      @cancel="onCancelEdit"
      @save="onSaveChanges"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)" />

    <AppResizableContainer :layout="isHorizontalLayout ? 'vertical' : 'horizontal'" :minSize="300">
      <template #headerTitle v-if="isHorizontalLayout">
        <nav
          role="section-nav"
          class="flex justify-between px-6 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9">
          <ul class="flex flex-wrap items-center gap-4 pl-4 -mb-px">
            <li
              v-for="tab in visibleSection1Tabs"
              :key="tab.name"
              @click="selectedTab = tab.name"
              class="py-1.5 cursor-pointer"
              :class="[
                selectedTab === tab.name ? 'border-b-2 border-theme-500' : '',
                tab.validator &&
                tab.validator.some((item) => {
                  return $vDocType[item].required?.$invalid || $vDocType[item].minValue?.invalid;
                })
                  ? 'text-red-500 !border-red-500'
                  : '',
              ]">
              <p>
                {{ tab.label }}
              </p>
            </li>
          </ul>
          <div v-if="documentType?.id" class="py-1.5 px-4 flex items-center justify-start gap-2">
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-medium text-white bg-theme-600 rounded hover:bg-theme-700"
              @click="onSetAsDefaultForAllCustomers">
              {{ $t('representative.setAsDefaultForAllCustomers') }}
            </button>
          </div>
        </nav>
      </template>
      <template #headerContent>
        <!-- Horizontal Layout -->
        <template v-if="isHorizontalLayout">
          <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
            <component
              :is="currentSection1Component"
              v-model="modifiedDocumentType"
              :mode="mode"
              :isHorizontalLayout="isHorizontalLayout"
              :key="`${selectedTab}-${documentType?.id}`"
              :name-error-message="
                $vDocType.name.required.$invalid ? $vDocType.name.required.$message : ''
              "
              :code-error-message="
                $vDocType.code.required.$invalid ? $vDocType.code.required.$message : ''
              "
              :division-error-message="
                $vDocType.division.required.$invalid ? $vDocType.division.required.$message : ''
              "
              :company-error-message="
                $vDocType.company.required.$invalid ? $vDocType.company.required.$message : ''
              "
              :maxDetailQtyErrorMessage="
                $vDocType.maxDetailQty.minValue.$invalid
                  ? $vDocType.maxDetailQty.minValue.$message
                  : ''
              "
              :maxDetailUnitPriceErrorMessage="
                $vDocType.maxDetailUnitPrice.minValue.$invalid
                  ? $vDocType.maxDetailUnitPrice.minValue.$message
                  : ''
              "
              :daysToDeliveryErrorMessage="
                $vDocType.daysToDelivery.minValue.$invalid
                  ? $vDocType.daysToDelivery.minValue.$message
                  : ''
              "
              :totalMeasurementUnitsMessage="
                $vDocType.totalMeasurementUnits.validRange.$invalid
                  ? $vDocType.totalMeasurementUnits.validRange.$message
                  : ''
              " />
          </PerfectScrollbar>
        </template>

        <!-- Vertical Layout -->
        <template v-else>
          <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
            <BaseAccordion
              v-for="tab in visibleSection1Tabs"
              :key="tab.name"
              :name="tab.label"
              :error="
                tab.validator &&
                tab.validator.some((item) => {
                  return $vDocType[item].required.$invalid;
                })
              ">
              <component
                :is="tab.component"
                v-model="modifiedDocumentType"
                :mode="mode"
                :isHorizontalLayout="isHorizontalLayout"
                :key="`${tab.name}-${documentType?.id}`"
                :name-error-message="
                  $vDocType.name.required.$invalid ? $vDocType.name.required.$message : ''
                "
                :code-error-message="
                  $vDocType.code.required.$invalid ? $vDocType.code.required.$message : ''
                "
                :division-error-message="
                  $vDocType.division.required.$invalid ? $vDocType.division.required.$message : ''
                "
                :company-error-message="
                  $vDocType.company.required.$invalid ? $vDocType.company.required.$message : ''
                "
                :maxDetailQtyErrorMessage="
                  $vDocType.maxDetailQty.minValue.$invalid
                    ? $vDocType.maxDetailQty.minValue.$message
                    : ''
                "
                :maxDetailUnitPriceErrorMessage="
                  $vDocType.maxDetailUnitPrice.minValue.$invalid
                    ? $vDocType.maxDetailUnitPrice.minValue.$message
                    : ''
                "
                :days-to-delivery-error-message="
                  $vDocType.daysToDelivery.minValue.$invalid
                    ? $vDocType.daysToDelivery.minValue.$message
                    : ''
                "
                :totalMeasurementUnitsMessage="
                  $vDocType.totalMeasurementUnits.validRange.$invalid
                    ? $vDocType.totalMeasurementUnits.validRange.$message
                    : ''
                " />
            </BaseAccordion>
          </PerfectScrollbar>
        </template>
      </template>
      <template #detailsTitle v-if="isHorizontalLayout">
        <nav
          role="section-nav"
          class="flex justify-between px-6 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9">
          <ul class="flex flex-wrap items-center gap-4 pl-4 -mb-px">
            <li
              v-for="tab in visibleSection2Tabs"
              :key="tab.name"
              @click="selectedTab2 = tab.name"
              class="py-1.5 cursor-pointer"
              :class="[selectedTab2 === tab.name ? 'border-b-2 border-theme-500' : '']">
              <p>
                {{ tab.label }}
              </p>
            </li>
          </ul>
        </nav>
      </template>
      <template #detailsContent>
        <!-- Horizontal Layout -->
        <template v-if="isHorizontalLayout">
          <PerfectScrollbar class="w-full h-full py-4 space-y-2">
            <component
              :is="currentSection2Component"
              v-model="modifiedDocumentType"
              :mode="mode"
              :isHorizontalLayout="isHorizontalLayout"
              :key="`${selectedTab2}-${documentType?.id}`"
              :isFromManager="documentType.isFromManager || false"
              :childDocumentTypes="childDocumentTypes"
              @delete-document-type="onDeleteRelatedDocumentType"
              @reload-data="loadChildDocumentTypes" />
          </PerfectScrollbar>
        </template>

        <!-- Vertical Layout -->
        <template v-else>
          <PerfectScrollbar class="w-full h-full py-4 space-y-2">
            <BaseAccordion v-for="tab in visibleSection2Tabs" :key="tab.name" :name="tab.label">
              <component
                :is="tab.component"
                v-model="modifiedDocumentType"
                :mode="mode"
                :isHorizontalLayout="isHorizontalLayout"
                :key="`${tab.name}-${documentType?.id}`"
                :isFromManager="documentType.isFromManager || false"
                :childDocumentTypes="childDocumentTypes"
                @delete-document-type="onDeleteRelatedDocumentType"
                @reload-data="loadChildDocumentTypes" />
            </BaseAccordion>
          </PerfectScrollbar>
        </template>
      </template>
    </AppResizableContainer>

    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="
        confirmModalActive = false;
        isBackMode = false;
      "
      @confirm="onDiscardChanges" />

    <BaseModalConfirm
      v-if="showSetDefaultConfirmation"
      @cancel="onCancelSetAsDefault"
      @confirm="onConfirmSetAsDefault">
      <template #title> {{ $t('representative.setAsDefaultForAllCustomers') }}</template>
      <template #text> {{ $t('representative.confirmSetAsDefaultForAllCustomers') }}</template>
      <template #buttonText> {{ $t('common.actions.confirm') }}</template>
    </BaseModalConfirm>
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useLookupsStore } from '@/stores/lookups';
import { useToastStore } from '@/stores/toast';

import DocumentTypeCreateEdit from '@/components/documents/DocumentTypeCreateEdit.vue';
import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import BaseAccordion from '@/components/common/BaseAccordion.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';

import {
  addOrEditDocumentType,
  getDocumentSeriesTypes,
  getDocumentTypeById,
  getDocumentTypes,
  setCustomerDefaultDocumentType,
} from '@/services/api/documents';
import type { DocumentType } from '@/types/DocumentTypes';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers, minValue } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import DocumentTypeDetailsTabTrader from '@/components/documents/DocumentTypeDetailsTabTrader.vue';
import DocumentTypeDetailsTabConfiguration from '@/components/documents/DocumentTypeDetailsTabConfiguration.vue';
import DocumentTypeDetailsTabDocumentSeries from '@/components/documents/DocumentTypeDetailsTabDocumentSeries.vue';
import DocumentTypeDetailsTabRelatedDocuments from '@/components/documents/DocumentTypeDetailsTabRelatedDocuments.vue';
import DocumentTypeDetailsTabColumnDefinitions from '@/components/documents/DocumentTypeDetailsTabColumnDefinitions.vue';

import { deepCopy } from '@/utils/data';
import { storeToRefs } from 'pinia';

const { reloadLookups, getCompaniesLookup } = useLookupsStore();
const { companies } = storeToRefs(useLookupsStore());
const route = useRoute();
const router = useRouter();
const toastStore = useToastStore();
const { t: $t } = useI18n();

const selectedTab = ref('properties');
const selectedTab2 = ref('documentSeries');
const isHorizontalLayout = ref(true);

type TabItem = {
  name: string;
  label: string;
  component: any;
  validator?: string[];
  hidden?: any;
};

const section1Tabs = ref<TabItem[]>([
  {
    name: 'properties',
    label: $t('document-type.properties'),
    component: markRaw(DocumentTypeCreateEdit),
    validator: ['name', 'code', 'division', 'company'],
  },
  {
    name: 'configuration',
    label: $t('document-type.configurationProperties'),
    component: markRaw(DocumentTypeDetailsTabConfiguration),
  },
  {
    name: 'trader',
    label: $t('document-type.trader'),
    component: markRaw(DocumentTypeDetailsTabTrader),
  },
  {
    name: 'columnDefinitions',
    label: $t('document-type.columnDefinitions'),
    component: markRaw(DocumentTypeDetailsTabColumnDefinitions),
    hidden: computed(() => !hasChildren.value),
  },
]);

const section2Tabs = ref<TabItem[]>([
  {
    name: 'documentSeries',
    label: $t('document-type.documentSeries'),
    component: markRaw(DocumentTypeDetailsTabDocumentSeries),
  },
  {
    name: 'relatedDocumentTypes',
    label: $t('document-type.relatedDocumentTypes'),
    component: markRaw(DocumentTypeDetailsTabRelatedDocuments),
    hidden: computed(() => !hasChildren.value),
  },
]);

const visibleSection1Tabs = computed(() => {
  return section1Tabs.value.filter((tab) => {
    if (tab.hidden && typeof tab.hidden !== 'boolean') {
      return !tab.hidden.value;
    }
    return !tab.hidden;
  });
});

const visibleSection2Tabs = computed(() => {
  return section2Tabs.value.filter((tab) => {
    if (tab.hidden && typeof tab.hidden !== 'boolean') {
      return !tab.hidden.value;
    }
    return !tab.hidden;
  });
});

const currentSection1Component = computed(() => {
  const selected = section1Tabs.value.find((tab) => tab.name === selectedTab.value);
  return selected ? selected.component : null;
});

const currentSection2Component = computed(() => {
  const selected = section2Tabs.value.find((tab) => tab.name === selectedTab2.value);
  return selected ? selected.component : null;
});

const documentType = ref<DocumentType>();
const modifiedDocumentType = ref<DocumentType>();
const isModified = ref<boolean>(false);
const mode = ref<'view' | 'edit'>('view');
const confirmModalActive = ref(false);
const isBackMode = ref(false);
const childDocumentTypes = ref<DocumentType[]>([]);
const childDocumentTypesToRemove = ref<DocumentType[]>([]);

const hasChildren = computed(() => childDocumentTypes.value.length > 0);

watch(
  () => modifiedDocumentType.value,
  (newData) => {
    isModified.value =
      JSON.stringify(newData) !== JSON.stringify(documentType.value) ||
      childDocumentTypesToRemove.value.length > 0;

    validationDocumentTypeModel.value.name = newData?.name || '';
    validationDocumentTypeModel.value.code = newData?.code || '';
    validationDocumentTypeModel.value.division = newData?.divisionID || '';
    validationDocumentTypeModel.value.company = newData?.companyID || '';
    validationDocumentTypeModel.value.maxDetailQty = newData?.maxDetailQty || 0;
    validationDocumentTypeModel.value.maxDetailUnitPrice = newData?.maxDetailUnitPrice || 0;
    validationDocumentTypeModel.value.daysToDelivery = newData?.daysToDelivery || 0;
    validationDocumentTypeModel.value.totalMeasurementUnits =
      newData?.totalMeasurementUnits ?? null;

    $vDocType.value.$touch();
  },
  { deep: true }
);

watch(
  () => childDocumentTypesToRemove.value.length,
  () => {
    isModified.value =
      JSON.stringify(modifiedDocumentType.value) !== JSON.stringify(documentType.value) ||
      childDocumentTypesToRemove.value.length > 0;
  }
);

onMounted(async () => {
  await getCompaniesLookup();
  await loadData();
});

async function loadData() {
  const responseDocumentType = await getDocumentTypeById(route.params.id as string);
  if (responseDocumentType) {
    documentType.value = responseDocumentType;
    const docSeriesOfType = await getDocumentSeriesTypes(route.params.id as string);
    documentType.value.documentSeriesType = docSeriesOfType;

    await loadChildDocumentTypes();
    childDocumentTypesToRemove.value = [];

    mode.value = 'view';
  } else {
    documentType.value = {
      id: route.params.id as string,
      isFromManager: true,
      isActive: true,
      code: '',
      name: '',
      description: '',
      takeDigitalSignature: false,
      mergedSameDocumentLines: false,
      usePaymentTypes: false,
      supportLoyalty: false,
      allowManualPrices: false,
      allowZeroQty: false,
      allowZeroValues: false,
      allowDynamicRange: false,
      allowEditClosedDocuments: false,
      companyID: companies.value[0].id,
      divisionID: '',
      defaultDocumentStatusID: '',
      defaultPaymentTypeID: '',
      defaultDeliveryTypeID: '',
      defaultTraderID: '',
      requireDeliveryType: false,
      requirePaymentType: false,
      requireWarehouse: false,
      allowHeaderDiscounts: false,
      documentSeriesType: [],
      syncWithMyData: false,
    };
    mode.value = 'edit';
    childDocumentTypes.value = [];
    childDocumentTypesToRemove.value = [];
  }
  modifiedDocumentType.value = deepCopy<DocumentType>(documentType.value);
}

async function loadChildDocumentTypes() {
  const children = await getDocumentTypes({
    documentTypeId: route.params.id as string,
  });
  childDocumentTypes.value = children || [];
}

function onDeleteRelatedDocumentType(docType: DocumentType) {
  if (!childDocumentTypesToRemove.value.find((dt) => dt.id === docType.id)) {
    childDocumentTypesToRemove.value.push(docType);
  }

  const index = childDocumentTypes.value.findIndex((dt) => dt.id === docType.id);
  if (index !== -1) {
    childDocumentTypes.value.splice(index, 1);
  }

  isModified.value = true;
}

function onCancelEdit(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
    return;
  } else if (!documentType.value?.createdDate) {
    router.push('/document-types');
  }
  mode.value = 'view';
}

async function onDiscardChanges() {
  mode.value = 'view';
  confirmModalActive.value = false;
  childDocumentTypesToRemove.value = [];
  if (documentType.value) {
    modifiedDocumentType.value = deepCopy<DocumentType>(documentType.value);
  }

  if (documentType.value?.id) {
    await loadChildDocumentTypes();
  }
  if (!documentType.value?.createdDate || isBackMode.value) {
    router.push('/document-types');
  }
}

async function onSaveChanges() {
  $vDocType.value.$touch();
  if ($vDocType.value.$invalid) return;

  mode.value = 'view';

  // Save main document type changes
  if (modifiedDocumentType.value) {
    let res = await addOrEditDocumentType(modifiedDocumentType.value);
    if (!res) {
      toastStore.error({ text: 'Failed to save document type' });
      return;
    }
  }

  if (childDocumentTypesToRemove.value.length > 0) {
    try {
      for (const docType of childDocumentTypesToRemove.value) {
        const updatedDocType: DocumentType = {
          ...docType,
          parentDocumentTypeId: undefined,
        };
        await addOrEditDocumentType(updatedDocType);
      }
      childDocumentTypesToRemove.value = [];
    } catch (error) {
      console.error('Error removing related document types:', error);
      toastStore.error({ text: $t('messages.errors.errorOccurred') });
      return;
    }
  }

  toastStore.success({ text: 'Successfully saved' });
  await reloadLookups(['documentTypes']);
  await loadData();

  if (!hasChildren.value && selectedTab2.value === 'relatedDocumentTypes') {
    selectedTab2.value = 'documentSeries';
  }
}

const validationDocumentTypeModel = ref({
  name: modifiedDocumentType.value?.name || '',
  code: modifiedDocumentType.value?.code || '',
  division: modifiedDocumentType.value?.divisionID || '',
  company: modifiedDocumentType.value?.companyID || '',
  maxDetailUnitPrice: modifiedDocumentType.value?.maxDetailUnitPrice || 0,
  maxDetailQty: modifiedDocumentType.value?.maxDetailQty || 0,
  daysToDelivery: modifiedDocumentType.value?.daysToDelivery || 0,
  totalMeasurementUnits: modifiedDocumentType.value?.totalMeasurementUnits || null,
});

const rulesDocType = computed(() => ({
  name: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  code: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  division: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  company: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  maxDetailUnitPrice: { minValue: minValue(0) },
  maxDetailQty: { minValue: minValue(0) },
  daysToDelivery: { minValue: minValue(0) },
  totalMeasurementUnits: {
    validRange: {
      $validator: (value: number | null | string) => {
        if (value === '' || value === null || value === undefined) {
          if (validationDocumentTypeModel.value) {
            validationDocumentTypeModel.value.totalMeasurementUnits = null;
          }
          return true;
        }
        return [1, 2, 3].includes(Number(value));
      },
      $message: () => $t('messages.errors.totalMeasurementUnitsRange'),
    },
  },
}));
const $vDocType = useVuelidate(rulesDocType, validationDocumentTypeModel, { $lazy: true });

const showSetDefaultConfirmation = ref(false);

function onSetAsDefaultForAllCustomers() {
  if (!documentType.value?.id) return;
  showSetDefaultConfirmation.value = true;
}

async function onConfirmSetAsDefault() {
  showSetDefaultConfirmation.value = false;

  if (!documentType.value?.id) return;

  await setCustomerDefaultDocumentType(documentType.value.id);
  toastStore.success({
    text: $t('representative.defaultDocumentTypeSetSuccessfully'),
  });
}

function onCancelSetAsDefault() {
  showSetDefaultConfirmation.value = false;
}
</script>
